<template>
  <div class="directory-node">
    <router-link
      tag="div"
      :to="'/me/scenarios/' + directory.uuid"
      :class="{ 'directory-row': true, selected: isCurrentDirectory }"
      :style="{ paddingLeft: 4 + depth * 16 + 'px' }"
      :title="directory.name"
      role="treeitem"
      :aria-selected="isCurrentDirectory ? 'true' : 'false'"
      :aria-expanded="hasChildren ? (expanded ? 'true' : 'false') : null"
    >
      <!-- Clicks on the controls are stopped so they don't trigger the surrounding router-links -->
      <button
        v-if="hasChildren"
        type="button"
        class="row-icon-button expand-button"
        :aria-label="(expanded ? 'Collapse ' : 'Expand ') + directory.name"
        @click.stop.prevent="expanded = !expanded"
      >
        <span class="material-icons">{{
          expanded ? "expand_more" : "chevron_right"
        }}</span>
      </button>
      <span v-else class="expand-spacer"></span>

      <span class="material-icons folder-icon">{{
        isCurrentDirectory ? "folder_open" : "folder"
      }}</span>
      <span class="directory-name">{{ directory.name }}</span>

      <div class="dropdown" @click.stop.prevent>
        <button
          type="button"
          class="row-icon-button folder-menu-button"
          :aria-label="'Folder options for ' + directory.name"
          aria-haspopup="true"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          @click.stop.prevent="toggleMenu"
        >
          <span class="material-icons">more_horiz</span>
        </button>
        <div
          :class="{ 'dropdown-menu dropdown-menu-right': true, show: menuOpen }"
        >
          <button
            type="button"
            class="dropdown-item"
            @click.stop.prevent="onCreate"
          >
            <span class="material-icons">create_new_folder</span>
            New subfolder
          </button>
          <button
            type="button"
            class="dropdown-item"
            @click.stop.prevent="onRename"
          >
            <span class="material-icons">edit</span>
            Rename
          </button>
          <button
            type="button"
            class="dropdown-item"
            @click.stop.prevent="onMove"
          >
            <span class="material-icons">drive_file_move</span>
            Move to…
          </button>
          <div class="dropdown-divider"></div>
          <button
            type="button"
            class="dropdown-item text-danger"
            @click.stop.prevent="onDelete"
          >
            <span class="material-icons">delete_outline</span>
            Delete
          </button>
        </div>
      </div>
    </router-link>

    <div v-if="hasChildren && expanded" role="group">
      <RecursiveDirectoryTree
        v-for="child in directory.children"
        :key="child.uuid"
        :directory="child"
        :parent-uuid="directory.uuid"
        :depth="depth + 1"
      />
    </div>
  </div>
</template>

<script>
import {
  createFolder,
  renameFolder,
  deleteFolder,
  moveToFolder,
} from "@/utils/folder.helper";

export default {
  name: "RecursiveDirectoryTree",
  props: {
    directory: Object,
    parentUuid: { type: String, default: null },
    depth: { type: Number, default: 0 },
  },
  data() {
    return {
      menuOpen: false,
      expanded: true,
    };
  },
  computed: {
    isCurrentDirectory() {
      return this.$route.params.directoryUuid === this.directory.uuid;
    },
    hasChildren() {
      return !!this.directory.children && this.directory.children.length > 0;
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
  },
  methods: {
    toggleMenu() {
      if (this.menuOpen) {
        this.closeMenu();
        return;
      }

      // Only one folder menu open at a time; other menus close on this document click
      document.dispatchEvent(new Event("click"));
      this.menuOpen = true;
      document.addEventListener("click", this.closeMenu);
    },
    closeMenu() {
      this.menuOpen = false;
      document.removeEventListener("click", this.closeMenu);
    },
    onCreate() {
      this.closeMenu();
      this.expanded = true;
      createFolder(this, this.directory.uuid);
    },
    onRename() {
      this.closeMenu();
      renameFolder(this, this.directory);
    },
    onMove() {
      this.closeMenu();
      moveToFolder(this, {
        type: "directory",
        uuid: this.directory.uuid,
        name: this.directory.name,
      });
    },
    onDelete() {
      this.closeMenu();
      deleteFolder(this, this.directory, this.parentUuid);
    },
  },
};
</script>

<style scoped>
.directory-row {
  display: flex;
  align-items: center;
  height: 32px;
  padding-right: 4px;
  margin: 1px 0;
  border-radius: 6px;
  color: var(--isim-light-text);
  font-size: 0.9rem;
  font-weight: 400;
  cursor: pointer;
  user-select: none;
}

.directory-row:hover {
  background: rgba(0, 0, 0, 0.04);
  color: #212529;
}

.directory-row.selected {
  /* --isim-primary at low opacity; --isim-primary-light is too faint to read as selected */
  background: rgba(89, 158, 216, 0.12);
  color: var(--isim-primary);
  font-weight: 600;
}

.row-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--isim-light-icon);
  cursor: pointer;
  flex-shrink: 0;
}

.row-icon-button:hover {
  background: rgba(0, 0, 0, 0.08);
  color: var(--isim-text);
}

/* Focus ring for keyboard users only, not after a mouse click */
.row-icon-button:focus {
  outline: none;
}

.row-icon-button:focus-visible {
  outline: 2px solid var(--isim-primary);
}

.row-icon-button .material-icons {
  font-size: 18px;
}

.expand-spacer {
  width: 22px;
  flex-shrink: 0;
}

.folder-icon {
  font-size: 18px;
  margin: 0 8px 0 2px;
  color: var(--isim-light-icon);
  flex-shrink: 0;
}

.directory-row.selected .folder-icon {
  color: var(--isim-primary);
}

.directory-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The options button only shows on hover or focus, so the tree stays uncluttered */
.folder-menu-button {
  opacity: 0;
}

.directory-row:hover .folder-menu-button,
.folder-menu-button:focus,
.folder-menu-button[aria-expanded="true"] {
  opacity: 1;
}

@media (hover: none) {
  .folder-menu-button {
    opacity: 1;
  }
}

.dropdown-menu {
  font-size: 0.9rem;
  font-weight: 400;
  min-width: 11rem;
  padding: 4px 0;
  border: none;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
}

.dropdown-item .material-icons {
  font-size: 18px;
  margin-right: 10px;
  color: var(--isim-light-icon);
}

.dropdown-item.text-danger .material-icons {
  color: inherit;
}
</style>
